/**
 * Main nav menu.
 *
 * @module
 */

import {$$} from '../../utilities/QuerySelectors.js';

export class Navigation {
    /**
     * Enable enhancements for the main nav menu in both desktop and mobile.
     */
    constructor() {
        /** @type {HTMLElement} */
        // @ts-ignore: cannot easily cast to HTMLElement
        this.container = document.querySelector('#main-menu');
        /** @type {HTMLElement} */
        // @ts-ignore: cannot easily cast to HTMLElement
        this.menu = document.querySelector('#mega-menu-main-menu-2020');
        /** @type {HTMLElement} */
        // @ts-ignore: cannot easily cast to HTMLElement
        this.topBar = document.querySelector('#mega-menu-header-buttons-2020');
        /** @type {HTMLElement} */
        // @ts-ignore: cannot easily cast to HTMLElement
        this.footer = document.querySelector('#footer-nav-menu');
    }

    /**
     * Show submenu content.
     *
     * @private
     * @param {HTMLElement} li Submenu `<li>` container.
     * @return {void}
     */
    expand(li) {
        /** @type {HTMLElement} */
        // @ts-ignore: cannot easily cast to HTMLElement
        const submenu = li.children[0];
        submenu.focus();
        document.body.dataset.hasSubmenuOpen = 'true';
    }

    /**
     * Hide submenu content.
     *
     * @private
     * @param {HTMLElement} li Submenu `<li>` container.
     * @return {void}
     */
    collapse(li) {
        /** @type {HTMLElement} */
        // @ts-ignore: cannot easily cast to HTMLElement
        const submenu = li.children[0];
        submenu.blur();
        document.body.dataset.hasSubmenuOpen = 'false';
    }

    /**
     * Toggle the submenu visibility on mouse hover.
     *
     * @private
     * @return {void}
     */
    initMouseToggle() {
        // @ts-ignore: window global
        hoverintent(
            this.menu,
            () => this.menu.classList.add('no-outline'),
            () => this.menu.classList.remove('no-outline')
        ).options({
            timeout: 10,
            interval: 100
        });
        /** @type {HTMLElement[]} */
        // @ts-ignore: cannot easily cast to HTMLElement
        const menuItems = Array.from(this.menu.children).filter(li => li.nodeName === 'LI');
        menuItems.forEach(li => {
            // @ts-ignore: window global
            hoverintent(
                li,
                () => this.expand(li),
                () => this.collapse(li)
            ).options({
                timeout: 10,
                interval: 100
            });
        });
    }

    /**
     * Initialize the main nav features for desktop.
     *
     * 1. Toggle the visibility on mouse hover.
     *
     * @return {void}
     */
    initDesktop() {
        if (!this.menu) {
            return;
        }
        this.initMouseToggle();
    }

    /**
     * Toggle a boolean WAI-Aria attribute on an element.
     *
     * @private
     * @param {HTMLElement} element
     * @param {string} attributeName
     * @return {void}
     */
    toggleAriaAttribute(element, attributeName) {
        const value = element.getAttribute(attributeName) !== 'true' ? 'true' : 'false';
        element.setAttribute(attributeName, value);
    }

    /**
     * Toggle the main nav menu visibility on mobile.
     *
     * @private
     * @param {HTMLElement} toggler
     * @return {void}
     */
    onToggleMenuClick(toggler) {
        this.toggleAriaAttribute(toggler, 'aria-selected');
        this.toggleAriaAttribute(this.container, 'aria-expanded');
    }

    /**
     * Collapse submenu text content for mobile.
     *
     * @private
     * @param {HTMLElement} widget
     * @param {HTMLElement} toggler
     * @return {void}
     */
    makeTextWidgetCollapsible(widget, toggler) {
        if (toggler) {
            // @ts-ignore: missing prop in definition
            toggler.ariaHasPopup = 'true';
            // @ts-ignore: missing prop in definition
            toggler.ariaExpanded = 'false';
            toggler.insertAdjacentHTML('beforeend', `<span class="mega-indicator"></span>`);
            toggler.addEventListener('click', () => {
                this.toggleAriaAttribute(toggler, 'aria-expanded');
            });
        }
    }

    /**
     * Collapse submenu content for mobile.
     *
     * @private
     * @return {void}
     */
    makeSubmenuContentCollapsible() {
        /** @type {HTMLElement[]} */
        // @ts-ignore: cannot easily cast to HTMLElement
        const textWidgets = $$('.mega-sub-menu .textwidget', this.menu);
        textWidgets.forEach(div => {
            /** @type {HTMLElement} */
            // @ts-ignore: cannot easily cast to HTMLElement
            const widgetToggler = div.previousElementSibling;
            this.makeTextWidgetCollapsible(div, widgetToggler);
        });
    }

    /**
     * Toggle submenu visibility for mobile on item tap.
     *
     * @private
     * @return {void}
     */
    makeSubmenusCollapsible() {
        $$('.mega-menu-link + .mega-sub-menu', this.container).forEach(ul => {
            /** @type {HTMLElement} */
            // @ts-ignore: cannot easily cast to HTMLElement
            const a = ul.previousElementSibling;
            a.addEventListener('click', e => {
                if (window.innerWidth <= 900) { 
                    // only disable clicks on mobile
                    e.preventDefault();
                }
                this.toggleAriaAttribute(a, 'aria-expanded');
            });
        });
    }

    /**
     * Clone login button into the mobile menu.
     *
     * @return {void}
     */
    initMobileAccountButtons() {
        if (!this.menu || !this.topBar) {
            return;
        }
        this.menu.lastElementChild.classList.add('last-desktop-item');
        $$('.mega-header-login, .mega-header-logout', this.topBar).forEach(li => {
            /** @type {HTMLElement} */
            // @ts-ignore: cannot easily cast to HTMLElement
            const loginButtonClone = li.cloneNode(true);
            this.menu.insertAdjacentElement('beforeend', loginButtonClone);
        });
    }

    /**
     * Initialize the main nav features for mobile.
     *
     * 1. Toggle the visibility on menu button click.
     * 2. Make submenus and content collapsed by default.
     *
     * @return {void}
     */
    initMobile() {
        if (!this.menu || !this.topBar) {
            return;
        }
        this.topBar.querySelector('.mega-toggle-menu a').addEventListener('click', e => {
            e.preventDefault();
            /** @type {HTMLElement} */
            // @ts-ignore: cannot easily cast to HTMLElement
            const togglerButton = e.target;
            this.onToggleMenuClick(togglerButton);
        });
        this.makeSubmenusCollapsible();
        this.makeSubmenuContentCollapsible();
    }

    /**
     * Is the menu item at the beginning of a line?
     *
     * @param {HTMLElement} item
     * @return {boolean}
     */
    isEdgeItem(item) {
        /** @type {HTMLElement} */
        // @ts-ignore: cannot easily cast to HTMLElement
        const prevItem = item.previousElementSibling;
        const isEdge = !prevItem || prevItem.offsetTop !== item.offsetTop;
        if (isEdge) {
            console.log('Navigation.isEdgeItem', 'yes', item.textContent, item.offsetTop, prevItem ? prevItem.offsetTop : 0);
        }
        return isEdge;
    }

    /**
     * Remove '|' from edge items.
     *
     * @param {HTMLElement} menu
     * @return {void}
     */
    removeEdgeItemSeparators(menu) {
        /** @type {HTMLElement[]} */
        // @ts-ignore: cannot easily cast to HTMLElement
        const items = $$('li', menu);
        items.forEach((li, i) => {
            setTimeout(() => {
                li.classList.toggle('has-sep', !this.isEdgeItem(li));
            }, (i + 1) * 100);
        });
    }

    /**
     * Initialize the footer nav features.
     *
     * 1. Remove separators from edge items.
     *
     * @return {void}
     */
    initFooter() {
        if (!this.footer) {
            return;
        }
        window.addEventListener('load', () => {
            this.removeEdgeItemSeparators(this.footer);
        });
    }
}
